(() => {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const track = (event, params = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  };

  const visibleFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );

  const initModal = (root) => {
    const overlay = root.querySelector('[data-pbx-tm-overlay]');
    if (!overlay) return null;

    const dialog = overlay.querySelector('[data-pbx-tm-dialog]');
    let lastFocused = null;

    const onKeydown = (event) => {
      if (!overlay.isConnected) {
        document.removeEventListener('keydown', onKeydown, true);
        document.documentElement.style.overflow = '';
        return;
      }
      if (!overlay.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = visibleFocusable(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const open = (trigger) => {
      if (overlay.classList.contains('is-open')) return;
      lastFocused = trigger || document.activeElement;
      overlay.classList.add('is-open');
      overlay.removeAttribute('aria-hidden');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown, true);

      const first = visibleFocusable(dialog)[0];
      if (first) first.focus();

      track('truemed_modal_open', {
        modal_source: trigger && trigger.dataset.pbxTmOpen ? trigger.dataset.pbxTmOpen : 'unknown',
      });
    };

    const close = () => {
      if (!overlay.classList.contains('is-open')) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKeydown, true);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    };

    root.querySelectorAll('[data-pbx-tm-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open(trigger);
      });
    });

    overlay.querySelectorAll('[data-pbx-tm-close]').forEach((btn) => {
      btn.addEventListener('click', close);
    });

    overlay.addEventListener('mousedown', (event) => {
      if (event.target === overlay) close();
    });

    const tabs = Array.from(overlay.querySelectorAll('[data-pbx-tm-tab]'));
    const panels = Array.from(overlay.querySelectorAll('[data-pbx-tm-panel]'));

    const selectTab = (tab, moveFocus) => {
      tabs.forEach((el) => {
        const active = el === tab;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-selected', active ? 'true' : 'false');
        el.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((panel) => {
        const active = panel.dataset.pbxTmPanel === tab.dataset.pbxTmTab;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(tab, false));
      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        selectTab(next, true);
      });
    });

    return close;
  };

  const initPurchaseOptions = (root) => {
    const group = root.querySelector('[data-pbx-purchase-options]');
    if (!group) return;

    const options = Array.from(group.querySelectorAll('[data-pbx-purchase-opt]'));

    group.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      options.forEach((option) => {
        option.classList.toggle(
          'pbx-purchase-opt--selected',
          option.contains(event.target)
        );
      });
    });
  };

  const initTracking = (root) => {
    const heroCta = root.querySelector('[data-pbx-hero-cta]');
    if (heroCta) {
      heroCta.addEventListener('click', () => {
        track('hero_cta_click', { link_url: heroCta.getAttribute('href') || '' });
      });
    }

    const quizCta = root.querySelector('[data-pbx-quiz-cta]');
    if (quizCta) {
      quizCta.addEventListener('click', () => {
        track('quiz_click', { link_url: quizCta.getAttribute('href') || '' });
      });
    }

    root.querySelectorAll('[data-pbx-sticky-link]').forEach((link) => {
      link.addEventListener('click', () => {
        track('sticky_bar_click', { link_label: link.dataset.pbxStickyLink });
      });
    });

    const mpRegion = root.querySelector('[data-pbx-mp-region]');
    if (mpRegion) {
      mpRegion.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="/products/"]');
        if (!link || !mpRegion.contains(link)) return;
        track('mp_graphic_pdp_click', {
          link_url: link.getAttribute('href') || '',
          link_text: (link.textContent || '').trim(),
        });
      });
    }
  };

  const teardowns = new WeakMap();

  const init = (root) => {
    if (!root || root.dataset.pbxReady === 'true') return;
    root.dataset.pbxReady = 'true';
    const closeModal = initModal(root);
    initPurchaseOptions(root);
    initTracking(root);
    if (closeModal) teardowns.set(root, closeModal);
  };

  const destroy = (root) => {
    const teardown = teardowns.get(root);
    if (!teardown) return;
    teardown();
    teardowns.delete(root);
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-pbx-root]').forEach(init);
  };

  const destroyAll = (scope) => {
    (scope || document).querySelectorAll('[data-pbx-root]').forEach(destroy);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
